import { Download, RotateCcw, Tag } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Avatar, Field, Modal, Spinner } from "../components/ui";
import { api, errorMessage, type HistoryEntry } from "../lib/api";
import { formatDate, timeAgo, toast } from "../lib/app";
import { computeDiff, type Row, type Seg } from "./linediff";

interface ChangedFile {
  status: "A" | "M" | "D";
  path: string;
}

const TEXTISH = /\.(tex|bib|cls|sty|bst|txt|md|csv|tsv|dat|tikz|lua|py|json|ya?ml|cfg|def|clo|ltx|bbx|cbx|lbx|r|m|sh|xml|gitignore|latexmkrc)$/i;

const fileName = (path: string) => path.split("/").pop()!;

// What a version did, said from the files it changed: "Edited intro.tex and 2 other files".
function describe(e: HistoryEntry) {
  if (!e.fileCount) return "No file changes";
  const files = [...e.files].sort((x, y) => Number(TEXTISH.test(y.path)) - Number(TEXTISH.test(x.path)));
  const verb = files.every((f) => f.status === "A") ? "Added" : files.every((f) => f.status === "D") ? "Deleted" : "Edited";
  const rest = e.fileCount - 1;
  if (!rest) return `${verb} ${fileName(files[0].path)}`;
  return `${verb} ${fileName(files[0].path)} and ${rest === 1 ? fileName(files[1].path) : `${rest} other files`}`;
}

const STATUS = { A: "new", D: "deleted", M: "" };

export function HistoryView({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState<string | "WORKING" | null>(null);
  const [compareCurrent, setCompareCurrent] = useState(false);
  const [files, setFiles] = useState<ChangedFile[] | null>(null);
  const [file, setFile] = useState<string | null>(null);
  const [labelFor, setLabelFor] = useState(false);
  const [more, setMore] = useState(true);

  const load = async (skip = 0) => {
    try {
      const res = await api.get<{ entries: HistoryEntry[]; dirty: boolean }>(`/api/projects/${projectId}/history?limit=60&skip=${skip}`);
      setEntries((prev) => (skip ? [...(prev ?? []), ...res.entries] : res.entries));
      setDirty(res.dirty);
      setMore(res.entries.length === 60);
      if (!skip && selected === null) setSelected(res.dirty ? "WORKING" : res.entries[0]?.sha ?? null);
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const entry = entries?.find((e) => e.sha === selected) ?? null;

  // Files changed in the selected version.
  useEffect(() => {
    if (!selected || !entries) return;
    setFiles(null);
    let url: string;
    if (selected === "WORKING") url = `/api/projects/${projectId}/history/changes?from=HEAD&to=WORKING`;
    else if (compareCurrent) url = `/api/projects/${projectId}/history/changes?from=${selected}&to=WORKING`;
    else url = `/api/projects/${projectId}/history/changes?to=${selected}`;
    api
      .get<{ files: ChangedFile[] }>(url)
      .then((r) => {
        setFiles(r.files);
        setFile((f) => (f && r.files.some((x) => x.path === f) ? f : r.files[0]?.path ?? null));
      })
      .catch((e) => toast(errorMessage(e), "error"));
  }, [selected, compareCurrent, entries, projectId]);

  const range = useMemo(() => {
    if (!selected) return null;
    if (selected === "WORKING") return { from: "HEAD", to: "WORKING" };
    if (compareCurrent) return { from: selected, to: "WORKING" };
    return { from: entry?.parents[0] ?? null, to: selected };
  }, [selected, compareCurrent, entry]);

  async function restore(path: string | null) {
    if (!selected || selected === "WORKING") return;
    const what = path ? `${path}` : "the whole project";
    if (!confirm(`Restore ${what} to ${entry ? `the version from ${formatDate(entry.date)}` : "this version"}? Current content is kept in history.`)) return;
    try {
      await api.post(`/api/projects/${projectId}/history/restore`, { rev: selected, path });
      toast(`Restored ${what}`, "success");
      setSelected(null);
      await load();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  async function deleteLabel(tag: string) {
    if (!confirm(`Delete label ${tag}?`)) return;
    await api.del(`/api/projects/${projectId}/history/labels?tag=${encodeURIComponent(tag)}`).catch((e) => toast(errorMessage(e), "error"));
    load();
  }

  // Group commits by day.
  const groups: Array<{ day: string; items: HistoryEntry[] }> = [];
  for (const e of entries ?? []) {
    const day = new Date(e.date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    if (groups[groups.length - 1]?.day !== day) groups.push({ day, items: [] });
    groups[groups.length - 1].items.push(e);
  }

  return (
    <div className="history">
      <div className="history-list">
        <div className="history-toolbar">
          <b className="grow">History</b>
          {canEdit && (
            <button className="btn btn-sm" onClick={() => setLabelFor(true)}>
              <Tag size={13} /> Label this version
            </button>
          )}
        </div>
        {!entries && (
          <div className="review-empty">
            <Spinner />
          </div>
        )}
        {dirty && (
          <>
            <div className="history-day">Now</div>
            <button className={`history-item${selected === "WORKING" ? " selected" : ""}`} onClick={() => setSelected("WORKING")}>
              <div className="subject">Latest changes</div>
              <div className="meta">Saved to history after a few quiet minutes</div>
            </button>
          </>
        )}
        {groups.map((g) => (
          <div key={g.day}>
            <div className="history-day">{g.day}</div>
            {g.items.map((e) => (
              <button key={e.sha} className={`history-item${selected === e.sha ? " selected" : ""}`} onClick={() => setSelected(e.sha)}>
                <div className="subject">{describe(e)}</div>
                <div className="meta row" style={{ gap: 6 }}>
                  <Avatar id={e.authorId ?? (e.authorEmail || e.authorName)} name={e.authorName} size={16} />
                  {e.authorName} · {new Date(e.date).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </div>
                {e.labels.map((l) => (
                  <span key={l.tag} className="label-tag">
                    <Tag size={10} /> {l.name}
                    {canEdit && (
                      <button
                        title="Delete label"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          deleteLabel(l.tag);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </button>
            ))}
          </div>
        ))}
        {entries && more && (
          <div style={{ padding: 12 }}>
            <button className="btn btn-sm" onClick={() => load(entries.length)}>
              Load older
            </button>
          </div>
        )}
      </div>

      <div className="history-detail">
        {selected && (
          <div className="history-toolbar">
            {entry ? (
              <div className="grow">
                <b>{describe(entry)}</b>
                <div className="muted small">
                  {entry.authorName} · {formatDate(entry.date)} ({timeAgo(entry.date)})
                </div>
              </div>
            ) : (
              <div className="grow">
                <b>Latest changes</b>
                <div className="muted small">Compared with the last saved version</div>
              </div>
            )}
            {entry && (
              <>
                <label className="check small">
                  <input type="checkbox" checked={compareCurrent} onChange={(e) => setCompareCurrent(e.target.checked)} />
                  Compare with current
                </label>
                <a className="btn btn-sm" href={`/api/projects/${projectId}/history/zip?rev=${entry.sha}`}>
                  <Download size={13} /> Zip
                </a>
                {canEdit && (
                  <button className="btn btn-sm" onClick={() => restore(null)}>
                    <RotateCcw size={13} /> Restore project
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {files && files.length > 0 && (
          <div className="history-files">
            {files.map((f) => (
              <button key={f.path} className={`file-pill${file === f.path ? " on" : ""}`} onClick={() => setFile(f.path)}>
                {f.path}
                {STATUS[f.status] && <span className={`st st-${f.status}`}>{STATUS[f.status]}</span>}
              </button>
            ))}
          </div>
        )}
        {files && files.length === 0 && <div className="diff-empty">No file changes in this version.</div>}
        {!files && selected && (
          <div className="diff-empty">
            <Spinner />
          </div>
        )}
        {range && file && files?.some((f) => f.path === file) && (
          <FileDiff
            projectId={projectId}
            from={range.from}
            to={range.to}
            path={file}
            status={files.find((f) => f.path === file)!.status}
            onRestore={canEdit && entry && !compareCurrent ? () => restore(file) : canEdit && entry && compareCurrent ? () => restore(file) : undefined}
          />
        )}
      </div>
      {labelFor && <LabelDialog projectId={projectId} onClose={() => setLabelFor(false)} onDone={() => load()} />}
    </div>
  );
}

function LabelDialog({ projectId, onClose, onDone }: { projectId: string; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await api.post(`/api/projects/${projectId}/history/labels`, { name });
      toast("Labelled", "success");
      onDone();
      onClose();
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Label this version"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!name.trim() || busy} onClick={save}>
            Save label
          </button>
        </>
      }
    >
      <Field label="Label" hint="Saves the current state as a named version (also sent to the server when the project syncs).">
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Draft sent to supervisor" onKeyDown={(e) => e.key === "Enter" && name.trim() && save()} />
      </Field>
    </Modal>
  );
}

type DiffRow = Row | { gap: number; start: number };

function Side({ segs, side }: { segs?: Seg[]; side: "old" | "new" }) {
  if (!segs) return <span className="diff-cell none" />;
  return <span className={`diff-cell ${side}`}>{segs.map(([changed, t], k) => (changed ? <mark key={k}>{t}</mark> : t))}</span>;
}

function FileDiff({
  projectId,
  from,
  to,
  path,
  status,
  onRestore,
}: {
  projectId: string;
  from: string | null;
  to: string;
  path: string;
  status: "A" | "M" | "D";
  onRestore?: () => void;
}) {
  const [texts, setTexts] = useState<{ a: string; b: string } | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const textual = TEXTISH.test(path);

  useEffect(() => {
    setTexts(null);
    setExpanded(new Set());
    if (!textual) return;
    const get = (rev: string | null) =>
      rev ? api.text(`/api/projects/${projectId}/history/file?rev=${encodeURIComponent(rev)}&path=${encodeURIComponent(path)}`).catch(() => "") : Promise.resolve("");
    Promise.all([status === "A" ? Promise.resolve("") : get(from), status === "D" ? Promise.resolve("") : get(to)]).then(([a, b]) => setTexts({ a, b }));
  }, [projectId, from, to, path, status, textual]);

  const { rows, changed } = useMemo(() => {
    const all = texts ? computeDiff(texts.a, texts.b) : [];
    const out: DiffRow[] = [];
    const CONTEXT = 3;
    let i = 0;
    while (i < all.length) {
      if (!all[i].same) {
        out.push(all[i++]);
        continue;
      }
      let j = i;
      while (j < all.length && all[j].same) j++;
      const runStart = i;
      const runEnd = j; // exclusive
      const keepHead = runStart === 0 ? 0 : CONTEXT;
      const keepTail = runEnd === all.length ? 0 : CONTEXT;
      if (runEnd - runStart > keepHead + keepTail + 2 && !expanded.has(runStart)) {
        for (let k = runStart; k < runStart + keepHead; k++) out.push(all[k]);
        out.push({ gap: runEnd - runStart - keepHead - keepTail, start: runStart });
        for (let k = runEnd - keepTail; k < runEnd; k++) out.push(all[k]);
      } else for (let k = runStart; k < runEnd; k++) out.push(all[k]);
      i = j;
    }
    return { rows: out, changed: all.some((r) => !r.same) };
  }, [texts, expanded]);

  return (
    <>
      <div className="history-toolbar">
        <span className="mono grow">{path}</span>
        <a className="btn btn-sm btn-ghost" href={`/api/projects/${projectId}/history/file?rev=${encodeURIComponent(status === "D" ? from ?? "" : to)}&path=${encodeURIComponent(path)}`} download={path.split("/").pop()}>
          <Download size={13} /> Download
        </a>
        {onRestore && status !== "D" && (
          <button className="btn btn-sm" onClick={onRestore}>
            <RotateCcw size={13} /> Restore this file
          </button>
        )}
      </div>
      {!textual ? (
        <div className="diff-empty">Binary file {status === "A" ? "added" : status === "D" ? "deleted" : "changed"}.</div>
      ) : !texts ? (
        <div className="diff-empty">
          <Spinner />
        </div>
      ) : !changed ? (
        <div className="diff-empty">No textual differences.</div>
      ) : (
        <div className="diff">
          <div className="diff-head">
            <span>Before</span>
            <span>After</span>
          </div>
          {rows.map((r, i) =>
            "gap" in r ? (
              <div key={i} className="diff-gap" onClick={() => setExpanded(new Set([...expanded, r.start]))}>
                ⋯ {r.gap} unchanged lines
              </div>
            ) : r.same ? (
              <div key={i} className="diff-row">
                <span className="diff-cell">{r.text}</span>
                <span className="diff-cell">{r.text}</span>
              </div>
            ) : (
              <div key={i} className="diff-row">
                <Side segs={r.old} side="old" />
                <Side segs={r.neu} side="new" />
              </div>
            ),
          )}
        </div>
      )}
    </>
  );
}
